import React from "react";
import { Progress } from "reactstrap";
import {
  LINEAR_SECTION_EYEBROW,
  RATINGS_HIDDEN_NOTE,
  buildLinearCoverageCount,
  buildResponseCountLabel,
} from "../../../../utils/triviaResultsStrings";

// Per-rating palette — keyed by the absolute rating value's offset from `min`
// (i.e. `colors[ratingValue - minValue]`) so the same rating uses the same
// color across every Linear question in the survey.
const COLORS = [
  "#4E79A7",
  "#F28E2B",
  "#E15759",
  "#76B7B2",
  "#59A14F",
  "#EDC948",
  "#B07AA1",
  "#FF9DA7",
  "#9C755F",
  "#BAB0AC",
];

const FALLBACK_COLOR = "#BAB0AC";

const LinearNumericBarGraph = ({
  xScales,
  yScales,
  totalSumOfNumResponses,
  question,
}) => {
  // Parent call-sites pass `xScales={xData}` (rating values, from
  // `xData.push(parseInt(itm))`) and `yScales={yData}` (response counts,
  // from `yData.push(sortedObj[itm])`). The prop names are kept unchanged
  // to avoid touching every call-site; we map them to clearer locals here.
  const responseCounts = yScales || [];
  const ratingValues = xScales || [];
  const totalResponses = totalSumOfNumResponses || 0;

  // Pull min/max from the question config when available; tolerate `question`
  // being undefined (the public results path does not pass it).
  const hasLinearConfig =
    question &&
    question.linearNumericConfigs &&
    typeof question.linearNumericConfigs === "object";

  const minValue =
    hasLinearConfig && typeof question.linearNumericConfigs.min === "number"
      ? question.linearNumericConfigs.min
      : 1;

  let maxValue;
  if (
    hasLinearConfig &&
    typeof question.linearNumericConfigs.max === "number"
  ) {
    maxValue = question.linearNumericConfigs.max;
  } else if (ratingValues.length > 0) {
    maxValue = Math.max.apply(Math, ratingValues);
  } else {
    maxValue = 10;
  }

  const totalRatings = maxValue - minValue + 1;

  // Build the per-rating rows (one entry for every rating in the scale, even
  // those with zero responses — the row list filters them out later, but the
  // total bar uses the full table so segment colors stay consistent).
  const rows = [];
  for (let idx = 0; idx < totalRatings; idx += 1) {
    const ratingValue = minValue + idx;
    const dataIdx = ratingValues.indexOf(ratingValue);
    const responseCount = dataIdx >= 0 ? responseCounts[dataIdx] : 0;
    const percentage =
      totalResponses > 0
        ? Math.round((responseCount / totalResponses) * 100)
        : 0;
    const color = COLORS[idx] || FALLBACK_COLOR;

    rows.push({
      ratingValue,
      responseCount,
      percentage,
      color,
    });
  }

  const answeredRows = rows.filter((row) => row.responseCount > 0);
  const answeredCount = answeredRows.length;
  const showFooterNote = answeredCount < totalRatings;

  return (
    <div className="tiv2_linear_body">
      {/* Section header row — eyebrow on the left, coverage count on the right. */}
      <div className="tiv2_linear_section_header">
        <span className="tiv2_section_eyebrow">{LINEAR_SECTION_EYEBROW}</span>
        <span className="tiv2_section_count">
          {buildLinearCoverageCount(answeredCount, totalRatings)}
        </span>
      </div>

      {/* Per-rating rows — only ratings that actually received responses. */}
      <div>
        {answeredRows.map((row) => (
          <div
            key={`row-${row.ratingValue}`}
            className="tiv2_linear_rating_row"
          >
            <span className="tiv2_linear_rating_label">{row.ratingValue}</span>
            <div
              className="tiv2_linear_rating_bar"
              style={{ "--tiv2-linear-bar-color": row.color }}
            >
              <Progress value={row.percentage} />
            </div>
            <span className="tiv2_linear_rating_pct">{row.percentage}%</span>
            <span className="tiv2_linear_rating_count">
              {buildResponseCountLabel(row.responseCount)}
            </span>
          </div>
        ))}
      </div>

      <div className="tiv2_linear_divider" />

      {showFooterNote && (
        <div className="tiv2_linear_footer_note">{RATINGS_HIDDEN_NOTE}</div>
      )}
    </div>
  );
};

export default LinearNumericBarGraph;
